import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import InputAdornment from "@mui/material/InputAdornment";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Autocomplete, {
  createFilterOptions,
} from "@mui/material/Autocomplete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import moment from "moment";
import toastr from "toastr";
import {
  makeGETRequestOnUserService,
  makePOSTRequestOnUserService,
} from "../../Api/api";
import { userServiceAxiosInstance } from "../../Api/setUserServiceToken";
import Listbox from "../../components/Listbox";
import { IUser } from "../../components/YieldCalculator/Interfaces";
import { BondOrderHistoryResponse } from "../../components/Users/Orders/helpers";
import { isVisibleLead } from "../../Helpers/adminPermissions";
import { downloadPDFFile } from "../../Helpers/functions";

const FORM_CODE = "121";
const FORM_LABEL = "Form No. 121";
const DOWNLOAD_FILE_PREFIX = `Form_${FORM_CODE}`;
const DEFAULT_RESIDENTIAL_STATUS = "INDIAN RESIDENT INDIVIDUAL";

interface ApiResponse<T> {
  status: number;
  data: T;
}

interface UserSelectionOption {
  uuid: string;
  fullName: string;
  mobileNumber: string;
  smestID: string;
  label: string;
  searchText: string;
}

interface UserProfile {
  uuid: string;
  firstName: string;
  lastName: string;
  accountType: string;
  emailId: string | null;
  mobileNumber: string | null;
  city: string | null;
  state: string | null;
  pincode: string | null;
  dateOfBirth: string | Date | null;
  dateOfIncorporationHuf: string | Date | null;
  hufPanCardName: string | null;
  panCard?: {
    name?: string | null;
    panCardNumber?: string | null;
  };
  proofOfAddress?: {
    address?: string | null;
    district_or_city?: string | null;
    state?: string | null;
    pincode?: string | null;
  };
}

interface InvestmentForm {
  security_name: string;
  file_path: string;
}

interface InvestmentFormsResponse {
  user_id: string;
  forms_by_year: {
    [year: string]: InvestmentForm[];
  };
}

interface DownloadGroup {
  fyStart: number;
  label: string;
  forms: InvestmentForm[];
}

interface FormInput {
  customerName: string;
  panCardNumber: string;
  birthDate: string;
  status: string;
  previousYear: string;
  residentialStatus: string;
  customerAddress: string;
  email: string;
  telephoneMobile: string;
  assessedToTax: string;
  latestAssessmentYear: string;
  estimatedIncomeDeclaration: string;
  estimatedTotalIncomePY: string;
  totalForm121Filed: string;
  aggregateIncomeForm121: string;
  sumofAggregate: string;
  taxYear1: string;
  ackNumber1: string;
  returnIncome1: string;
  taxYear2: string;
  ackNumber2: string;
  returnIncome2: string;
  investmentAccountNumber: string;
  isSeniorCitizen: boolean;
}

type HiddenFieldName = "assessedToTax" | "latestAssessmentYear" | "isSeniorCitizen";
type VisibleFieldName = Exclude<keyof FormInput, HiddenFieldName>;

interface FieldConfig {
  label: string;
  name: VisibleFieldName;
  type: "text" | "number" | "date";
  required?: boolean;
  multiline?: boolean;
  rows?: number;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
}

interface TabPanelProps {
  children: React.ReactNode;
  value: number;
  index: number;
}

const DEFAULT_FORM_VALUES: FormInput = {
  customerName: "",
  panCardNumber: "",
  birthDate: "",
  status: "",
  previousYear: "",
  residentialStatus: DEFAULT_RESIDENTIAL_STATUS,
  customerAddress: "",
  email: "",
  telephoneMobile: "",
  assessedToTax: "No",
  latestAssessmentYear: "",
  estimatedIncomeDeclaration: "",
  estimatedTotalIncomePY: "",
  totalForm121Filed: "0",
  aggregateIncomeForm121: "0",
  sumofAggregate: "0",
  taxYear1: "",
  ackNumber1: "",
  returnIncome1: "",
  taxYear2: "",
  ackNumber2: "",
  returnIncome2: "",
  investmentAccountNumber: "",
  isSeniorCitizen: false,
};

const AMOUNT_FIELDS = new Set<VisibleFieldName>([
  "estimatedIncomeDeclaration",
  "aggregateIncomeForm121",
  "sumofAggregate",
  "estimatedTotalIncomePY",
  "returnIncome1",
  "returnIncome2",
]);

const userFilter = createFilterOptions<UserSelectionOption>({
  ignoreCase: true,
  ignoreAccents: true,
  limit: 100,
  matchFrom: "any",
  stringify: (option) => option.searchText,
});

const investmentFilter = createFilterOptions<string>({
  ignoreCase: true,
  ignoreAccents: true,
  matchFrom: "any",
});

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null}
  </div>
);

const getCurrentTaxYear = () => {
  const year = new Date().getFullYear();
  return `${year}-${year + 1}`;
};

const parseTaxYearEndYear = (value: string) => {
  const trimmedValue = value.trim();
  const match = trimmedValue.match(/^(\d{4})\s*-\s*(\d{2}|\d{4})$/);

  if (!match) {
    return null;
  }

  const startYear = Number(match[1]);
  const endYearRaw = Number(match[2]);

  if (match[2].length === 4) {
    return endYearRaw;
  }

  const century = Math.floor(startYear / 100) * 100;
  const startYearShort = startYear % 100;

  return endYearRaw < startYearShort
    ? century + 100 + endYearRaw
    : century + endYearRaw;
};

const parseTaxYearStartYear = (value: string) => {
  const trimmedValue = value.trim();
  const match = trimmedValue.match(/^(\d{4})\s*-\s*(\d{2}|\d{4})$/);
  return match ? Number(match[1]) : null;
};

const getTaxYearRange = (value: string) => {
  const startYear = parseTaxYearStartYear(value);
  const endYear = parseTaxYearEndYear(value);

  if (!startYear || !endYear) {
    return null;
  }

  const start = moment(`${startYear}-04-01`, "YYYY-MM-DD", true);
  const end = moment(`${endYear}-03-31`, "YYYY-MM-DD", true);

  if (!start.isValid() || !end.isValid()) {
    return null;
  }

  return { start, end };
};

const parseNumberInput = (value: string) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const getInvestmentIsin = (investment: string) =>
  investment.split(" - ")[0]?.trim() || "";

const getInvestmentSecurityName = (investment: string) =>
  investment.split(" - ").slice(1).join(" - ").trim();

const getCashflowMoment = (rawDate: string) => {
  const parsed = moment(
    rawDate,
    ["YYYY-MM-DD", "DD/MM/YYYY", "DD-MM-YYYY", moment.ISO_8601],
    true
  );

  if (parsed.isValid()) {
    return parsed;
  }

  const fallback = moment(rawDate);
  return fallback.isValid() ? fallback : null;
};

const normalizeDateForPayload = (value: string) => {
  const parsed = moment(
    value,
    ["YYYY-MM-DD", "DD MMM, YYYY", "DD/MM/YYYY", "DD-MM-YYYY", moment.ISO_8601],
    true
  );

  if (parsed.isValid()) {
    return parsed.format("YYYY-MM-DD");
  }

  return value.trim() || undefined;
};

const isSeniorCitizenForTaxYear = (
  dateOfBirth: string | Date | null | undefined,
  taxYear: string
) => {
  if (!dateOfBirth) {
    return false;
  }

  const birthDate = moment(dateOfBirth, moment.ISO_8601, true);
  const taxYearEndYear = parseTaxYearEndYear(taxYear);

  if (!birthDate.isValid() || !taxYearEndYear) {
    return false;
  }

  const taxYearEndDate = moment(`${taxYearEndYear}-03-31`, "YYYY-MM-DD", true);
  return taxYearEndDate.diff(birthDate, "years") >= 60;
};

const formatBirthOrIncorporationDate = (
  profile: UserProfile | null,
  outputFormat = "YYYY-MM-DD"
) => {
  if (!profile) {
    return "";
  }

  const relevantDate =
    profile.accountType === "Hindu Undivided Family"
      ? profile.dateOfIncorporationHuf
      : profile.dateOfBirth;

  const formattedDate = moment(relevantDate, moment.ISO_8601, true);
  return formattedDate.isValid() ? formattedDate.format(outputFormat) : "";
};

const getConsolidatedAddress = (profile: UserProfile | null) => {
  if (!profile) {
    return "";
  }

  return [
    profile.proofOfAddress?.address,
    profile.proofOfAddress?.district_or_city || profile.city,
    profile.proofOfAddress?.state || profile.state,
    profile.proofOfAddress?.pincode || profile.pincode,
    "India",
  ]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(", ");
};

const getDefaultAssesseeName = (profile: UserProfile | null) => {
  if (!profile) {
    return "";
  }

  return (
    profile.hufPanCardName ||
    profile.panCard?.name ||
    [profile.firstName, profile.lastName].filter(Boolean).join(" ")
  );
};

const getRelevantDateLabel = (accountType: string | undefined) =>
  accountType === "Hindu Undivided Family"
    ? "Date of Incorporation of HUF"
    : "Date of Birth";

const getGeneratedFormSecurityLabel = (securityName: string) =>
  securityName.replace(/ - \d{2} \w+, \d{4} \(\d{2}:\d{2}:\d{2} [AP]M\)$/, "").trim();

const getAmountAdornment = (fieldName: VisibleFieldName) =>
  AMOUNT_FIELDS.has(fieldName) ? (
    <InputAdornment position="start">₹</InputAdornment>
  ) : undefined;

const getErrorStatus = (error: unknown) => {
  if (
    error &&
    typeof error === "object" &&
    "response" in error &&
    error.response &&
    typeof error.response === "object" &&
    "status" in error.response
  ) {
    return Number(error.response.status);
  }

  return undefined;
};

const buildPayload = ({
  data,
  investment,
  selectedUserUuid,
}: {
  data: FormInput;
  investment: string;
  selectedUserUuid: string;
}) => ({
  isin: getInvestmentIsin(investment),
  name_of_assessee: data.customerName,
  pan_number: data.panCardNumber,
  status: data.status,
  residential_status: data.residentialStatus,
  address: data.customerAddress,
  email: data.email,
  mobile_number: data.telephoneMobile,
  previous_year: data.previousYear,
  latest_assessment_year: data.latestAssessmentYear,
  estimated_income_for_declaration: parseNumberInput(
    data.estimatedIncomeDeclaration
  ),
  estimated_total_income_p_y: parseNumberInput(data.estimatedTotalIncomePY),
  total_15g_forms_filed: parseNumberInput(data.totalForm121Filed),
  aggregate_income_for_15g: parseNumberInput(data.aggregateIncomeForm121),
  income_amount: parseNumberInput(data.estimatedIncomeDeclaration),
  is_assessed_to_tax: data.assessedToTax === "Yes",
  nature_of_income: "INTEREST FORM NCD",
  tax_section: "193",
  user_id: selectedUserUuid,
  income_tax_act_1961: data.assessedToTax === "Yes",
  form_type: FORM_CODE,
  is_above_60: data.isSeniorCitizen,
  sum_of_aggregate: parseNumberInput(data.sumofAggregate),
  tax_year_1: data.taxYear1,
  ack_number_1: data.ackNumber1,
  return_income_1: parseNumberInput(data.returnIncome1),
  tax_year_2: data.taxYear2,
  ack_number_2: data.ackNumber2,
  return_income_2: parseNumberInput(data.returnIncome2),
  birth_date: normalizeDateForPayload(data.birthDate),
});

const Form121 = () => {
  const [tabValue, setTabValue] = useState(0);
  const [userOpen, setUserOpen] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [users, setUsers] = useState<UserSelectionOption[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserSelectionOption | null>(
    null
  );
  const [profileLoading, setProfileLoading] = useState(false);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [orders, setOrders] = useState<BondOrderHistoryResponse[]>([]);
  const [formsLoading, setFormsLoading] = useState(false);
  const [formsData, setFormsData] = useState<InvestmentFormsResponse | null>(
    null
  );
  const [formsRefreshNonce, setFormsRefreshNonce] = useState(0);
  const [generateInvestment, setGenerateInvestment] = useState("");
  const [downloadInvestment, setDownloadInvestment] = useState("");
  const [formValues, setFormValues] = useState<FormInput>({
    ...DEFAULT_FORM_VALUES,
    previousYear: getCurrentTaxYear(),
  });
  const [generating, setGenerating] = useState(false);
  const [downloadingFilePath, setDownloadingFilePath] = useState<string | null>(
    null
  );
  const [hasOverriddenSeniorCitizen, setHasOverriddenSeniorCitizen] =
    useState(false);
  const [hasOverriddenEstimatedIncome, setHasOverriddenEstimatedIncome] =
    useState(false);
  const [hasOverriddenSumOfAggregate, setHasOverriddenSumOfAggregate] =
    useState(false);
  const estimatedIncomePrefillKeyRef = useRef<string | null>(null);
  const sumOfAggregatePrefillKeyRef = useRef<string | null>(null);

  const fieldConfigs = useMemo<FieldConfig[]>(
    () => [
      {
        label: "Name",
        name: "customerName",
        type: "text",
      },
      {
        label: "Permanent Account Number",
        name: "panCardNumber",
        type: "text",
      },
      {
        label: getRelevantDateLabel(profile?.accountType),
        name: "birthDate",
        type: "date",
      },
      {
        label: "Status",
        name: "status",
        type: "text",
      },
      {
        label: "Tax Year (for which declaration is made)",
        name: "previousYear",
        type: "text",
      },
      {
        label: "Residential Status",
        name: "residentialStatus",
        type: "text",
      },
      {
        label: "Email ID",
        name: "email",
        type: "text",
      },
      {
        label: "Contact Number",
        name: "telephoneMobile",
        type: "text",
      },
      {
        label: "Address",
        name: "customerAddress",
        type: "text",
        multiline: true,
        rows: 3,
      },
      {
        label: "Estimated Income for which declaration is made",
        name: "estimatedIncomeDeclaration",
        type: "number",
        inputMode: "decimal",
      },
      {
        label: "Total number of Form No. 121 filed earlier",
        name: "totalForm121Filed",
        type: "number",
        inputMode: "numeric",
      },
      {
        label: "Aggregate amount of income for which Form No. 121 were filed",
        name: "aggregateIncomeForm121",
        type: "number",
        inputMode: "decimal",
      },
      {
        label:
          "Aggregate amount of income for which declaration is made during the tax year",
        name: "sumofAggregate",
        type: "number",
        inputMode: "decimal",
      },
      {
        label:
          "Estimated total income of the tax year including the Aggregate amount of income for which declaration is made during the tax year",
        name: "estimatedTotalIncomePY",
        type: "number",
        inputMode: "decimal",
      },
      {
        label: "Tax Year 1",
        name: "taxYear1",
        type: "text",
        required: false,
      },
      {
        label: "Acknowledgement Number 1",
        name: "ackNumber1",
        type: "text",
        required: false,
      },
      {
        label: "Return Income 1",
        name: "returnIncome1",
        type: "number",
        required: false,
        inputMode: "decimal",
      },
      {
        label: "Tax Year 2",
        name: "taxYear2",
        type: "text",
        required: false,
      },
      {
        label: "Acknowledgement Number 2",
        name: "ackNumber2",
        type: "text",
        required: false,
      },
      {
        label: "Return Income 2",
        name: "returnIncome2",
        type: "number",
        required: false,
        inputMode: "decimal",
      },
      {
        label: "Identification number for relevant Investment/Account etc.",
        name: "investmentAccountNumber",
        type: "text",
      },
    ],
    [profile?.accountType]
  );

  const fieldConfigByName = useMemo(
    () =>
      new Map<VisibleFieldName, FieldConfig>(
        fieldConfigs.map((fieldConfig) => [fieldConfig.name, fieldConfig])
      ),
    [fieldConfigs]
  );

  const loadUsers = useCallback(async () => {
    if (usersLoading || usersLoaded) {
      return;
    }

    setUsersLoading(true);
    try {
      const response = (await makeGETRequestOnUserService(
        "/user/user_list/"
      )) as ApiResponse<IUser[]>;

      if (response.status === 200) {
        const options = response.data
          .filter((user) => isVisibleLead(user.leadowner))
          .map((user) => {
            const fullName = [user.firstName, user.lastName]
              .filter(Boolean)
              .join(" ");
            const smestId = user.smestID || "--";
            const mobileNumber = user.mobileNumber || "--";

            return {
              uuid: user.uuid,
              fullName,
              mobileNumber,
              smestID: smestId,
              label: `${fullName} - ${mobileNumber}`,
              searchText: `${fullName} ${mobileNumber} ${smestId}`.toLowerCase(),
            };
          });

        setUsers(options);
        setUsersLoaded(true);
      }
    } catch (error) {
      toastr.error("Failed to fetch users.");
    } finally {
      setUsersLoading(false);
    }
  }, [usersLoaded, usersLoading]);

  const initializeFormFromProfile = useCallback((nextProfile: UserProfile | null) => {
    const defaultTaxYear = getCurrentTaxYear();

    setFormValues({
      ...DEFAULT_FORM_VALUES,
      customerName: getDefaultAssesseeName(nextProfile),
      panCardNumber: nextProfile?.panCard?.panCardNumber || "",
      birthDate: formatBirthOrIncorporationDate(nextProfile),
      status: nextProfile?.accountType || "",
      previousYear: defaultTaxYear,
      residentialStatus: DEFAULT_RESIDENTIAL_STATUS,
      customerAddress: getConsolidatedAddress(nextProfile),
      email: nextProfile?.emailId || "",
      telephoneMobile: nextProfile?.mobileNumber || "",
      investmentAccountNumber: "",
      isSeniorCitizen:
        nextProfile?.accountType === "Individual Resident"
          ? isSeniorCitizenForTaxYear(nextProfile.dateOfBirth, defaultTaxYear)
          : false,
    });
    setHasOverriddenSeniorCitizen(false);
    setHasOverriddenEstimatedIncome(false);
    setHasOverriddenSumOfAggregate(false);
    estimatedIncomePrefillKeyRef.current = null;
    sumOfAggregatePrefillKeyRef.current = null;
  }, []);

  const fetchUserOrders = useCallback(async (userUuid: string) => {
    const response = (await makeGETRequestOnUserService(
      `order_management_v2/orders/history/${userUuid}/`
    )) as ApiResponse<BondOrderHistoryResponse[]>;

    return response.data || [];
  }, []);

  useEffect(() => {
    if (!selectedUser?.uuid) {
      setProfile(null);
      setOrders([]);
      setFormsData(null);
      setGenerateInvestment("");
      setDownloadInvestment("");
      initializeFormFromProfile(null);
      return;
    }

    let mounted = true;
    setGenerateInvestment("");
    setDownloadInvestment("");
    initializeFormFromProfile(null);
    setFormsData(null);
    setProfileLoading(true);
    setOrdersLoading(true);

    const fetchData = async () => {
      const [profileResult, ordersResult] = await Promise.allSettled([
        makeGETRequestOnUserService(
          `user/get/${selectedUser.uuid}/`
        ) as Promise<ApiResponse<UserProfile>>,
        fetchUserOrders(selectedUser.uuid),
      ]);

      if (!mounted) {
        return;
      }

      if (profileResult.status === "fulfilled") {
        setProfile(profileResult.value.data);
        initializeFormFromProfile(profileResult.value.data);
      } else {
        setProfile(null);
        toastr.error("Failed to fetch selected user profile.");
      }

      if (ordersResult.status === "fulfilled") {
        setOrders(ordersResult.value);
      } else {
        setOrders([]);
        toastr.error("Failed to fetch selected user investments.");
      }

      setProfileLoading(false);
      setOrdersLoading(false);
    };

    void fetchData();

    return () => {
      mounted = false;
    };
  }, [fetchUserOrders, initializeFormFromProfile, selectedUser?.uuid]);

  useEffect(() => {
    if (!selectedUser?.uuid) {
      return;
    }

    let mounted = true;
    setFormsLoading(true);

    const fetchForms = async () => {
      try {
        const response = (await makeGETRequestOnUserService(
          `order_management_v2/get_investment_form/${selectedUser.uuid}/`
        )) as ApiResponse<InvestmentFormsResponse>;

        if (mounted) {
          setFormsData(response.data);
        }
      } catch (error) {
        if (!mounted) {
          return;
        }

        setFormsData({
          user_id: selectedUser.uuid,
          forms_by_year: {},
        });

        if (getErrorStatus(error) !== 404) {
          toastr.error("Failed to fetch generated forms.");
        }
      } finally {
        if (mounted) {
          setFormsLoading(false);
        }
      }
    };

    void fetchForms();

    return () => {
      mounted = false;
    };
  }, [formsRefreshNonce, selectedUser?.uuid]);

  useEffect(() => {
    setFormValues((currentState) =>
      currentState.investmentAccountNumber === generateInvestment
        ? currentState
        : {
            ...currentState,
            investmentAccountNumber: generateInvestment,
          }
    );
  }, [generateInvestment]);

  useEffect(() => {
    if (
      !profile ||
      profile.accountType !== "Individual Resident" ||
      hasOverriddenSeniorCitizen
    ) {
      return;
    }

    const nextValue = isSeniorCitizenForTaxYear(
      profile.dateOfBirth,
      formValues.previousYear
    );

    setFormValues((currentState) =>
      currentState.isSeniorCitizen === nextValue
        ? currentState
        : {
            ...currentState,
            isSeniorCitizen: nextValue,
          }
    );
  }, [formValues.previousYear, hasOverriddenSeniorCitizen, profile]);

  const investmentOptions = useMemo(() => {
    return Array.from(
      new Set(
        orders
          .filter(
            (order) =>
              order.deal_status === "Settled" &&
              !(order.is_delete && order.order_deleted_date)
          )
          .sort((a, b) => {
            const dateA = new Date(a.insertedAt);
            const dateB = new Date(b.insertedAt);
            return dateB.getTime() - dateA.getTime();
          })
          .map((order) => `${order.isin} - ${order.security_name}`)
      )
    );
  }, [orders]);

  const estimatedIncomePrefill = useMemo(() => {
    if (!generateInvestment) {
      return "";
    }

    const taxYearRange = getTaxYearRange(formValues.previousYear);
    const selectedIsin = getInvestmentIsin(generateInvestment);

    if (!taxYearRange || !selectedIsin) {
      return "";
    }

    const totalInterest = orders
      .filter(
        (order) =>
          order.deal_status === "Settled" && order.isin?.trim() === selectedIsin
      )
      .reduce((sum, order) => {
        const cashflows = Array.isArray(order.cashflows) ? order.cashflows : [];

        return (
          sum +
          cashflows.reduce((orderSum, cashflow) => {
            if (cashflow.type !== "Interest") {
              return orderSum;
            }

            const cashflowDate = getCashflowMoment(cashflow.date);
            if (
              !cashflowDate ||
              cashflowDate.isBefore(taxYearRange.start, "day") ||
              cashflowDate.isAfter(taxYearRange.end, "day")
            ) {
              return orderSum;
            }

            return orderSum + parseNumberInput(String(cashflow.amount));
          }, 0)
        );
      }, 0);

    return totalInterest.toFixed(2);
  }, [formValues.previousYear, generateInvestment, orders]);

  useEffect(() => {
    if (!generateInvestment) {
      estimatedIncomePrefillKeyRef.current = null;
      setHasOverriddenEstimatedIncome(false);
      return;
    }

    const prefillKey = `${generateInvestment}::${formValues.previousYear}`;
    const hasContextChanged = estimatedIncomePrefillKeyRef.current !== prefillKey;

    if (!hasContextChanged && hasOverriddenEstimatedIncome) {
      return;
    }

    setFormValues((currentState) =>
      currentState.estimatedIncomeDeclaration === estimatedIncomePrefill
        ? currentState
        : {
            ...currentState,
            estimatedIncomeDeclaration: estimatedIncomePrefill,
          }
    );

    estimatedIncomePrefillKeyRef.current = prefillKey;

    if (hasContextChanged) {
      setHasOverriddenEstimatedIncome(false);
    }
  }, [
    estimatedIncomePrefill,
    formValues.previousYear,
    generateInvestment,
    hasOverriddenEstimatedIncome,
  ]);

  const computedSumOfAggregate = useMemo(
    () =>
      (
        parseNumberInput(formValues.estimatedIncomeDeclaration) +
        parseNumberInput(formValues.aggregateIncomeForm121)
      ).toFixed(2),
    [formValues.aggregateIncomeForm121, formValues.estimatedIncomeDeclaration]
  );

  useEffect(() => {
    const currentContext = generateInvestment
      ? `${generateInvestment}::${formValues.previousYear}`
      : null;

    if (!currentContext) {
      sumOfAggregatePrefillKeyRef.current = null;
      setHasOverriddenSumOfAggregate(false);
      return;
    }

    const hasContextChanged =
      sumOfAggregatePrefillKeyRef.current !== currentContext;

    if (!hasContextChanged && hasOverriddenSumOfAggregate) {
      return;
    }

    setFormValues((currentState) =>
      currentState.sumofAggregate === computedSumOfAggregate
        ? currentState
        : {
            ...currentState,
            sumofAggregate: computedSumOfAggregate,
          }
    );

    sumOfAggregatePrefillKeyRef.current = currentContext;

    if (hasContextChanged) {
      setHasOverriddenSumOfAggregate(false);
    }
  }, [
    computedSumOfAggregate,
    formValues.previousYear,
    generateInvestment,
    hasOverriddenSumOfAggregate,
  ]);

  const downloadGroups = useMemo<DownloadGroup[]>(() => {
    if (!formsData?.forms_by_year) {
      return [];
    }

    const allForms = Object.values(formsData.forms_by_year).flat();
    const filteredByType = allForms.filter((form) =>
      form.file_path.includes(DOWNLOAD_FILE_PREFIX)
    );

    const financialYearMap: Record<number, InvestmentForm[]> = {};
    filteredByType.forEach((form) => {
      const match = form.security_name.match(
        /- (.+\(\d{2}:\d{2}:\d{2} [AP]M\))$/
      );
      const dateLabel = match ? match[1] : "";
      const parsedDate = moment(dateLabel, "DD MMMM, YYYY (hh:mm:ss A)");
      const financialYearStart =
        parsedDate.month() >= 3 ? parsedDate.year() : parsedDate.year() - 1;

      if (!financialYearMap[financialYearStart]) {
        financialYearMap[financialYearStart] = [];
      }

      financialYearMap[financialYearStart].push(form);
    });

    return Object.entries(financialYearMap)
      .map(([financialYearStart, forms]) => {
        const fyStart = Number(financialYearStart);
        const sortedForms = [...forms].sort((firstForm, secondForm) => {
          const firstDateMatch = firstForm.security_name.match(/- (.+\))$/);
          const secondDateMatch = secondForm.security_name.match(/- (.+\))$/);
          const firstDate = moment(
            firstDateMatch?.[1] || "",
            "DD MMMM, YYYY (hh:mm:ss A)"
          );
          const secondDate = moment(
            secondDateMatch?.[1] || "",
            "DD MMMM, YYYY (hh:mm:ss A)"
          );

          return secondDate.valueOf() - firstDate.valueOf();
        });

        return {
          fyStart,
          label: `1st April ${fyStart} to 31st March ${fyStart + 1}`,
          forms: sortedForms,
        };
      })
      .sort((firstGroup, secondGroup) => secondGroup.fyStart - firstGroup.fyStart);
  }, [formsData]);

  const filteredDownloadGroups = useMemo(() => {
    if (!downloadInvestment) {
      return downloadGroups;
    }

    const selectedSecurityName = getInvestmentSecurityName(downloadInvestment);

    return downloadGroups
      .map((group) => ({
        ...group,
        forms: group.forms.filter(
          (form) =>
            getGeneratedFormSecurityLabel(form.security_name) ===
            selectedSecurityName
        ),
      }))
      .filter((group) => group.forms.length > 0);
  }, [downloadGroups, downloadInvestment]);

  const updateFormValue = useCallback(
    <K extends keyof FormInput>(fieldName: K, value: FormInput[K]) => {
      if (fieldName === "estimatedIncomeDeclaration") {
        setHasOverriddenEstimatedIncome(true);
      }

      if (fieldName === "sumofAggregate") {
        setHasOverriddenSumOfAggregate(true);
      }

      if (fieldName === "isSeniorCitizen") {
        setHasOverriddenSeniorCitizen(true);
      }

      setFormValues((currentState) => ({
        ...currentState,
        [fieldName]: value,
      }));
    },
    []
  );

  const renderEditableField = useCallback(
    (fieldName: VisibleFieldName) => {
      const fieldConfig = fieldConfigByName.get(fieldName);

      if (!fieldConfig) {
        return null;
      }

      return (
        <TextField
          fullWidth
          variant="outlined"
          label={fieldConfig.label}
          type={fieldConfig.type}
          value={formValues[fieldConfig.name]}
          onChange={(event) =>
            updateFormValue(fieldConfig.name, event.target.value)
          }
          required={fieldConfig.required !== false}
          multiline={fieldConfig.multiline}
          rows={fieldConfig.rows}
          inputProps={{
            inputMode: fieldConfig.inputMode,
          }}
          InputProps={{
            startAdornment: getAmountAdornment(fieldConfig.name),
          }}
          InputLabelProps={
            fieldConfig.type === "date" ? { shrink: true } : undefined
          }
        />
      );
    },
    [fieldConfigByName, formValues, updateFormValue]
  );

  const validateForm = useCallback(() => {
    const missingField = fieldConfigs.find(
      (fieldConfig) =>
        fieldConfig.required !== false &&
        !String(formValues[fieldConfig.name] || "").trim()
    );

    if (missingField) {
      toastr.error(`${missingField.label} is required.`);
      return false;
    }

    return true;
  }, [fieldConfigs, formValues]);

  const handleGenerateForm = useCallback(async () => {
    if (!selectedUser?.uuid || !generateInvestment) {
      toastr.error("Please select a user and investment first.");
      return;
    }

    if (!validateForm()) {
      return;
    }

    setGenerating(true);
    try {
      await makePOSTRequestOnUserService(
        "order_management_v2/create_user_investment_form/",
        buildPayload({
          data: formValues,
          investment: generateInvestment,
          selectedUserUuid: selectedUser.uuid,
        })
      );

      toastr.success(
        `${FORM_LABEL} generated successfully. Please download it from the download tab.`
      );
      setDownloadInvestment(generateInvestment);
      setFormsRefreshNonce((currentValue) => currentValue + 1);
      setTabValue(1);
    } catch (error) {
      const status = getErrorStatus(error);
      if (status) {
        toastr.error(`Failed to generate ${FORM_LABEL}. (${status})`);
      } else {
        toastr.error(`Failed to generate ${FORM_LABEL}.`);
      }
    } finally {
      setGenerating(false);
    }
  }, [formValues, generateInvestment, selectedUser?.uuid, validateForm]);

  const handleDownloadForm = useCallback(
    async (form: InvestmentForm) => {
      if (!selectedUser?.uuid) {
        toastr.error("Please select a user first.");
        return;
      }

      setDownloadingFilePath(form.file_path);
      try {
        const response = await userServiceAxiosInstance.get<Blob>(
          "/order_management_v2/presigned-url/",
          {
            params: {
              user_id: selectedUser.uuid,
              file_path: form.file_path,
            },
            responseType: "blob",
          }
        );

        await downloadPDFFile(
          response.data,
          `${DOWNLOAD_FILE_PREFIX} ${form.security_name}.pdf`
        );
        toastr.success(`${FORM_LABEL} downloaded successfully.`);
      } catch (error) {
        const status = getErrorStatus(error);
        if (status) {
          toastr.error(`Failed to download ${FORM_LABEL}. (${status})`);
        } else {
          toastr.error(`Failed to download ${FORM_LABEL}.`);
        }
      } finally {
        setDownloadingFilePath(null);
      }
    },
    [selectedUser?.uuid]
  );

  const renderInvestmentSelector = (
    value: string,
    onChange: (nextValue: string) => void,
    label: string
  ) => (
    <Autocomplete
      value={value || null}
      options={investmentOptions}
      loading={ordersLoading}
      filterOptions={investmentFilter}
      ListboxComponent={Listbox}
      popupIcon={<ExpandMoreIcon />}
      onChange={(_, newValue) => onChange(newValue || "")}
      renderOption={(props, option) => (
        <Box component="li" {...props}>
          <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
            {option}
          </Typography>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          fullWidth
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {ordersLoading ? (
                  <CircularProgress color="inherit" size={18} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      noOptionsText="No settled investments found"
    />
  );

  const renderGenerateTab = () => {
    if (profileLoading || ordersLoading) {
      return (
        <Box
          sx={{
            minHeight: 240,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          <CircularProgress />
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Loading user details and investments...
          </Typography>
        </Box>
      );
    }

    if (!profile) {
      return (
        <Box sx={{ py: 4 }}>
          <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
            Unable to load form data.
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Please reselect the user and try again.
          </Typography>
        </Box>
      );
    }

    return (
      <>
        <Box sx={{ width: ["100%", "70%", "60%"], mb: 3 }}>
          {renderInvestmentSelector(
            generateInvestment,
            setGenerateInvestment,
            "Select investment"
          )}
        </Box>

        {!generateInvestment ? (
          <Box sx={{ py: 4 }}>
            <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
              Select an investment to generate {FORM_LABEL}.
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              The form is prefilled from the selected user and investment.
            </Typography>
          </Box>
        ) : (
          <Box
            component="form"
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              void handleGenerateForm();
            }}
            sx={{
              p: 2.5,
              borderRadius: 3,
              bgcolor: "#fafafa",
              border: "1px solid rgba(15, 23, 42, 0.08)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1.5,
                mb: 3,
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 800, mb: 0.5 }}>
                  {generateInvestment}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Review the details before generating the form.
                </Typography>
              </Box>
            </Box>

            <Grid container spacing={2}>
              {fieldConfigs.map((fieldConfig) => {
                if (
                  fieldConfig.name === "aggregateIncomeForm121" ||
                  fieldConfig.name === "sumofAggregate" ||
                  fieldConfig.name === "ackNumber1" ||
                  fieldConfig.name === "returnIncome1" ||
                  fieldConfig.name === "ackNumber2" ||
                  fieldConfig.name === "returnIncome2"
                ) {
                  return null;
                }

                if (fieldConfig.name === "estimatedIncomeDeclaration") {
                  return (
                    <Grid item xs={12} key="income-calculation-block">
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: { xs: "column", md: "row" },
                          alignItems: { xs: "stretch", md: "center" },
                          gap: 1.5,
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          {renderEditableField("estimatedIncomeDeclaration")}
                        </Box>

                        <Box
                          sx={{
                            alignSelf: "center",
                            minWidth: { xs: "100%", md: 40 },
                            display: "flex",
                            justifyContent: "center",
                          }}
                        >
                          <Typography
                            sx={{
                              color: "#334155",
                              fontSize: 22,
                              fontWeight: 900,
                              lineHeight: 1,
                            }}
                          >
                            +
                          </Typography>
                        </Box>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          {renderEditableField("aggregateIncomeForm121")}
                        </Box>

                        <Box
                          sx={{
                            alignSelf: "center",
                            minWidth: { xs: "100%", md: 40 },
                            display: "flex",
                            justifyContent: "center",
                          }}
                        >
                          <Typography
                            sx={{
                              color: "#1d4ed8",
                              fontSize: 22,
                              fontWeight: 900,
                              lineHeight: 1,
                            }}
                          >
                            =
                          </Typography>
                        </Box>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          {renderEditableField("sumofAggregate")}
                        </Box>
                      </Box>
                    </Grid>
                  );
                }

                if (fieldConfig.name === "taxYear1") {
                  return (
                    <Grid item xs={12} key="itr-details-row-1">
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: { xs: "1fr", md: "1fr 1.5fr 1fr" },
                          gap: 2,
                          alignItems: "start",
                        }}
                      >
                        <Box>{renderEditableField("taxYear1")}</Box>
                        <Box>{renderEditableField("ackNumber1")}</Box>
                        <Box>{renderEditableField("returnIncome1")}</Box>
                      </Box>
                    </Grid>
                  );
                }

                if (fieldConfig.name === "taxYear2") {
                  return (
                    <Grid item xs={12} key="itr-details-row-2">
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: { xs: "1fr", md: "1fr 1.5fr 1fr" },
                          gap: 2,
                          alignItems: "start",
                        }}
                      >
                        <Box>{renderEditableField("taxYear2")}</Box>
                        <Box>{renderEditableField("ackNumber2")}</Box>
                        <Box>{renderEditableField("returnIncome2")}</Box>
                      </Box>
                    </Grid>
                  );
                }

                return (
                  <Grid
                    item
                    xs={12}
                    sm={
                      fieldConfig.name === "customerAddress" ||
                      fieldConfig.name === "estimatedTotalIncomePY"
                        ? 12
                        : fieldConfig.name === "investmentAccountNumber"
                          ? 6
                          : 4
                    }
                    md={
                      fieldConfig.name === "customerAddress"
                        ? 12
                        : fieldConfig.name === "estimatedTotalIncomePY"
                          ? 9
                          : fieldConfig.name === "investmentAccountNumber"
                            ? 12
                            : 3
                    }
                    key={fieldConfig.name}
                  >
                    {renderEditableField(fieldConfig.name)}
                  </Grid>
                );
              })}

              {profile.accountType === "Individual Resident" ? (
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!formValues.isSeniorCitizen}
                        onChange={(event) =>
                          updateFormValue("isSeniorCitizen", event.target.checked)
                        }
                        sx={{ p: 0.5 }}
                      />
                    }
                    label="If resident individual, whether age is 60 years or more at any time of the year"
                    sx={{
                      mx: 0,
                      my: 0.25,
                      gap: 1,
                      alignItems: "center",
                      ".MuiFormControlLabel-label": {
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#2a324c",
                        lineHeight: 1.35,
                      },
                    }}
                  />
                </Grid>
              ) : null}
            </Grid>

            <Button
              type="submit"
              variant="contained"
              disabled={generating}
              sx={{
                mt: 3,
                py: 1.25,
                px: 3,
                fontWeight: 700,
              }}
            >
              {generating ? (
                <>
                  <CircularProgress
                    size={18}
                    sx={{ color: "#fff", mr: 1.25 }}
                  />
                  Generating {FORM_LABEL}
                </>
              ) : (
                `Generate ${FORM_LABEL}`
              )}
            </Button>
          </Box>
        )}
      </>
    );
  };

  const renderDownloadTab = () => {
    if (formsLoading) {
      return (
        <Box
          sx={{
            minHeight: 240,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          <CircularProgress />
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Loading generated forms...
          </Typography>
        </Box>
      );
    }

    return (
      <>
        <Box sx={{ width: ["100%", "70%", "60%"], mb: 3 }}>
          {renderInvestmentSelector(
            downloadInvestment,
            setDownloadInvestment,
            "Filter by investment"
          )}
        </Box>

        {filteredDownloadGroups.length === 0 ? (
          <Box sx={{ py: 4 }}>
            <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
              {downloadInvestment
                ? `No generated ${FORM_LABEL} found for the selected investment.`
                : `No generated ${FORM_LABEL} found for the selected user.`}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Generate the form from the first tab, then come back here to
              download it.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={3}>
            {filteredDownloadGroups.map((group) => (
              <Box key={group.fyStart}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1.5,
                    mb: 1.5,
                    flexWrap: "wrap",
                  }}
                >
                  <Typography sx={{ fontWeight: 800, fontSize: 18 }}>
                    {group.label}
                  </Typography>
                  <Chip
                    label={`${group.forms.length} form${group.forms.length > 1 ? "s" : ""}`}
                    variant="outlined"
                  />
                </Box>

                <Stack spacing={1.25}>
                  {group.forms.map((form) => {
                    const isDownloading =
                      downloadingFilePath === form.file_path;

                    return (
                      <Box
                        key={form.file_path}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 2,
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor: "#fafafa",
                          border: "1px solid rgba(15, 23, 42, 0.08)",
                          flexWrap: "wrap",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ flex: 1, minWidth: 280, fontWeight: 500 }}
                        >
                          {form.security_name}
                        </Typography>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => void handleDownloadForm(form)}
                          disabled={isDownloading}
                          startIcon={
                            isDownloading ? (
                              <CircularProgress size={16} sx={{ color: "#fff" }} />
                            ) : (
                              <DownloadRoundedIcon />
                            )
                          }
                        >
                          {isDownloading ? "Downloading..." : "Download"}
                        </Button>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </>
    );
  };

  return (
    <Box sx={{ maxWidth: "92%", mx: "auto", my: 4, zoom: 0.95 }}>
      <Paper sx={{ p: [2, 3], borderRadius: 4 }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "flex-start",
            justifyContent: "space-between",
            mb: 3,
          }}
        >
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
              Form 121
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Generate and download {FORM_LABEL} for a selected user.
            </Typography>
          </Box>

          <Box sx={{ width: ["100%", 520] }}>
            <Autocomplete
              id="form-121-user-select"
              value={selectedUser}
              open={userOpen}
              options={users}
              loading={usersLoading}
              onOpen={() => {
                setUserOpen(true);
                void loadUsers();
              }}
              onClose={() => setUserOpen(false)}
              onChange={(_, newValue) => setSelectedUser(newValue)}
              isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
              getOptionLabel={(option) => option.label}
              filterOptions={userFilter}
              ListboxComponent={Listbox}
              popupIcon={<ExpandMoreIcon />}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                      {option.fullName}
                    </Typography>
                    <Typography sx={{ color: "text.secondary", fontSize: 12 }}>
                      {option.mobileNumber} | SMEST ID: {option.smestID}
                    </Typography>
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select user by name or mobile number"
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {usersLoading ? (
                          <CircularProgress color="inherit" size={18} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>
        </Box>

        {selectedUser ? (
          <>
            <Box
              sx={{
                mb: 3,
                p: 2,
                borderRadius: 3,
                bgcolor: "rgba(59, 130, 246, 0.08)",
                display: "flex",
                flexWrap: "wrap",
                gap: 1.5,
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 700 }}>
                  {selectedUser.fullName}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {selectedUser.mobileNumber} | SMEST ID: {selectedUser.smestID}
                </Typography>
              </Box>

              <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                {profile?.accountType ? (
                  <Chip label={profile.accountType} color="primary" variant="outlined" />
                ) : null}
              </Stack>
            </Box>

            <Tabs
              value={tabValue}
              onChange={(_, nextValue) => setTabValue(nextValue)}
              variant="scrollable"
              sx={{
                borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
                ".MuiTab-root": {
                  fontWeight: 700,
                  textTransform: "none",
                },
              }}
            >
              <Tab label={`Generate ${FORM_LABEL}`} />
              <Tab label={`Download ${FORM_LABEL}`} />
            </Tabs>

            <TabPanel value={tabValue} index={0}>
              {renderGenerateTab()}
            </TabPanel>
            <TabPanel value={tabValue} index={1}>
              {renderDownloadTab()}
            </TabPanel>
          </>
        ) : (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
              Select a user to continue.
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              The generate and download sections will load after a user is
              selected.
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default Form121;
